import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import PasswordInput from '../../components/ui/PasswordInput';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { User } from '../../types';
import {
  buildRoleValue,
  canManageUsers,
  getRoleLabel,
  normalizeRole,
  parseRole,
} from '../../utils/roles';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: string;
}

interface ManageFormState {
  role:
    | 'developer'
    | 'system_admin'
    | 'finance_manager'
    | 'general_manager'
    | 'sales_manager'
    | 'salesperson'
    | 'other';
  status: 'active' | 'inactive' | 'suspended';
}

interface AddUserFormState {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  role: ManageFormState['role'];
  status: ManageFormState['status'];
}

const permissionOptions = [
  { value: 'users:view', label: 'View Users' },
  { value: 'users:manage', label: 'Manage Users' },
  { value: 'deals:view', label: 'View Deals' },
  { value: 'deals:manage', label: 'Manage Deals' },
  { value: 'reports:view', label: 'View Reports' },
  { value: 'settings:manage', label: 'Manage Settings' },
];

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [manageForm, setManageForm] = useState<ManageFormState | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState<AddUserFormState | null>(null);
  const [otherRoleLabel, setOtherRoleLabel] = useState('');
  const [otherRolePermissions, setOtherRolePermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<User[]>('/users/');

        // Map API response to UserRow format
        const mappedUsers: UserRow[] = response.map((user) => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          role: user.role,
          status: user.status,
          lastLogin: user.last_login_at
            ? new Date(user.last_login_at).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : 'Never',
        }));

        setUsers(mappedUsers);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    return users.filter((user) => {
      const matchesTerm =
        !normalizedTerm ||
        user.name.toLowerCase().includes(normalizedTerm) ||
        user.email.toLowerCase().includes(normalizedTerm);
      const matchesRole = !roleFilter || normalizeRole(user.role) === roleFilter;
      const matchesStatus = !statusFilter || user.status === statusFilter;
      return matchesTerm && matchesRole && matchesStatus;
    });
  }, [searchTerm, roleFilter, statusFilter, users]);

  const canManageUser = (targetUser: UserRow): boolean => {
    const currentRole = normalizeRole(currentUser?.role);
    const targetRole = normalizeRole(targetUser.role);

    if (!canManageUsers(currentRole)) return false;
    if (targetRole === 'developer' && currentRole !== 'developer') {
      return false;
    }
    return true;
  };

  const openManageModal = (targetUser: UserRow) => {
    if (!canManageUser(targetUser)) {
      toast.error('You do not have permission to manage this user.');
      return;
    }
    const parsedRole = parseRole(targetUser.role);
    setSelectedUser(targetUser);
    setManageForm({
      role: parsedRole.baseRole,
      status: targetUser.status,
    });
    setOtherRoleLabel(parsedRole.customLabel || '');
    setOtherRolePermissions(parsedRole.permissions);
  };

  const closeManageModal = () => {
    setSelectedUser(null);
    setManageForm(null);
    setOtherRoleLabel('');
    setOtherRolePermissions([]);
  };

  const openAddUserModal = () => {
    const currentRole = normalizeRole(currentUser?.role);
    if (!canManageUsers(currentRole)) {
      toast.error('You do not have permission to add users.');
      return;
    }

    setShowAddUserModal(true);
    setAddUserForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'salesperson',
      status: 'active',
    });
    setOtherRoleLabel('');
    setOtherRolePermissions([]);
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    setAddUserForm(null);
    setOtherRoleLabel('');
    setOtherRolePermissions([]);
  };

  const handleCreateUser = async () => {
    if (!addUserForm) return;

    const currentRole = normalizeRole(currentUser?.role);
    if (!canManageUsers(currentRole)) {
      toast.error('You do not have permission to add users.');
      return;
    }

    if (!addUserForm.first_name.trim() || !addUserForm.last_name.trim()) {
      toast.error('Please provide first and last name.');
      return;
    }

    const normalizedEmail = addUserForm.email.trim().toLowerCase();
    if (!normalizedEmail) {
      toast.error('Please provide an email address.');
      return;
    }

    if (!addUserForm.password.trim() || addUserForm.password.trim().length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }

    if (addUserForm.role === 'developer' && currentRole !== 'developer') {
      toast.error('Only Developer can create Developer accounts.');
      return;
    }

    if (addUserForm.role === 'other' && !otherRoleLabel.trim()) {
      toast.error('Please provide a custom label for Other role.');
      return;
    }

    const roleValue = buildRoleValue(addUserForm.role, otherRoleLabel, otherRolePermissions);

    try {
      setSaving(true);
      const createdUser = await apiService.post<User>('/users/', {
        email: normalizedEmail,
        first_name: addUserForm.first_name.trim(),
        last_name: addUserForm.last_name.trim(),
        password: addUserForm.password.trim(),
        role: roleValue,
        status: addUserForm.status,
      });

      const mappedUser: UserRow = {
        id: createdUser.id,
        name: `${createdUser.first_name} ${createdUser.last_name}`,
        email: createdUser.email,
        role: createdUser.role,
        status: createdUser.status,
        lastLogin: createdUser.last_login_at
          ? new Date(createdUser.last_login_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : 'Never',
      };

      setUsers((previous) => [mappedUser, ...previous]);
      toast.success('User created successfully.');
      closeAddUserModal();
    } catch {
      toast.error('Failed to create user.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManage = async () => {
    if (!selectedUser || !manageForm) return;

    const currentRole = normalizeRole(currentUser?.role);
    const isSelf = selectedUser.id === currentUser?.id;

    if (manageForm.role === 'developer' && currentRole !== 'developer') {
      toast.error('Only Developer can assign the Developer role.');
      return;
    }

    if (currentRole === 'system_admin' && isSelf && manageForm.role !== 'system_admin') {
      toast.error('System Admin cannot change their own role.');
      return;
    }

    if (manageForm.role === 'other' && !otherRoleLabel.trim()) {
      toast.error('Please provide a custom label for Other role.');
      return;
    }

    const roleValue = buildRoleValue(manageForm.role, otherRoleLabel, otherRolePermissions);

    try {
      setSaving(true);
      const updatedUser = await apiService.put<User>(`/users/${selectedUser.id}`, {
        role: roleValue,
        status: manageForm.status,
      });

      setUsers((previous) =>
        previous.map((entry) => {
          if (entry.id !== selectedUser.id) return entry;
          return {
            ...entry,
            role: updatedUser.role,
            status: updatedUser.status,
          };
        })
      );

      toast.success('User updated successfully.');
      closeManageModal();
    } catch {
      toast.error('Failed to update user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProtectedRoute roles={['system_admin', 'general_manager', 'sales_manager', 'finance_manager']}>
      <div>
        {/* Header */}
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage your dealership staff and their permissions.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={openAddUserModal}
              disabled={!canManageUsers(normalizeRole(currentUser?.role))}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add User
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="card mb-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="form-input pl-10"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <select
              className="form-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="system_admin">System Admin</option>
              <option value="finance_manager">Finance Manager</option>
              <option value="general_manager">General Manager</option>
              <option value="sales_manager">Sales Manager</option>
              <option value="salesperson">Salesperson</option>
              <option value="other">Other</option>
              {normalizeRole(currentUser?.role) === 'developer' && (
                <option value="developer">Developer</option>
              )}
            </select>
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        {/* User list */}
        <div className="card">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading users...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-xl">⚠</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Users</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button className="btn-primary" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PlusIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching users</h3>
              <p className="text-gray-500 mb-6">
                Try clearing filters or add a team member to get started.
              </p>
              <button className="btn-primary">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Team Member
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Name</th>
                    <th className="table-header-cell">Email</th>
                    <th className="table-header-cell">Role</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Last Login</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="table-row">
                      <td className="table-cell font-medium text-gray-900">{user.name}</td>
                      <td className="table-cell text-gray-600">{user.email}</td>
                      <td className="table-cell text-gray-600">{getRoleLabel(user.role)}</td>
                      <td className="table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            user.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : user.status === 'inactive'
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="table-cell text-gray-600">{user.lastLogin}</td>
                      <td className="table-cell">
                        <button
                          onClick={() => openManageModal(user)}
                          disabled={!canManageUser(user)}
                          className="text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedUser && manageForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900">Manage User</h3>
              <p className="mt-1 text-sm text-gray-600">
                {selectedUser.name} ({selectedUser.email})
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                  <select
                    className="form-input"
                    value={manageForm.role}
                    onChange={(e) =>
                      setManageForm({
                        ...manageForm,
                        role: e.target.value as ManageFormState['role'],
                      })
                    }
                    disabled={
                      normalizeRole(currentUser?.role) === 'system_admin' &&
                      selectedUser.id === currentUser?.id
                    }
                  >
                    {normalizeRole(currentUser?.role) === 'developer' && (
                      <option value="developer">Developer</option>
                    )}
                    <option value="system_admin">System Admin</option>
                    <option value="finance_manager">Finance Manager</option>
                    <option value="general_manager">General Manager</option>
                    <option value="sales_manager">Sales Manager</option>
                    <option value="salesperson">Salesperson</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {manageForm.role === 'other' && (
                  <div className="space-y-3 rounded-md border border-gray-200 p-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Other Role Label
                      </label>
                      <input
                        className="form-input"
                        value={otherRoleLabel}
                        onChange={(e) => setOtherRoleLabel(e.target.value)}
                        placeholder="e.g. Inventory Specialist"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Permissions
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {permissionOptions.map((permission) => (
                          <label
                            key={permission.value}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={otherRolePermissions.includes(permission.value)}
                              onChange={(e) => {
                                setOtherRolePermissions((previous) => {
                                  if (e.target.checked) {
                                    return [...previous, permission.value];
                                  }
                                  return previous.filter((item) => item !== permission.value);
                                });
                              }}
                            />
                            {permission.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="form-input"
                    value={manageForm.status}
                    onChange={(e) =>
                      setManageForm({
                        ...manageForm,
                        status: e.target.value as ManageFormState['status'],
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={closeManageModal}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveManage}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddUserModal && addUserForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900">Add User</h3>
              <p className="mt-1 text-sm text-gray-600">Create a new dealership staff account.</p>

              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      className="form-input"
                      value={addUserForm.first_name}
                      onChange={(e) =>
                        setAddUserForm({ ...addUserForm, first_name: e.target.value })
                      }
                      placeholder="First"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      className="form-input"
                      value={addUserForm.last_name}
                      onChange={(e) =>
                        setAddUserForm({ ...addUserForm, last_name: e.target.value })
                      }
                      placeholder="Last"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={addUserForm.email}
                    onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                    placeholder="name@dealership.com"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Temporary Password
                  </label>
                  <PasswordInput
                    className="form-input"
                    value={addUserForm.password}
                    onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                  <select
                    className="form-input"
                    value={addUserForm.role}
                    onChange={(e) =>
                      setAddUserForm({
                        ...addUserForm,
                        role: e.target.value as AddUserFormState['role'],
                      })
                    }
                  >
                    {normalizeRole(currentUser?.role) === 'developer' && (
                      <option value="developer">Developer</option>
                    )}
                    <option value="system_admin">System Admin</option>
                    <option value="finance_manager">Finance Manager</option>
                    <option value="general_manager">General Manager</option>
                    <option value="sales_manager">Sales Manager</option>
                    <option value="salesperson">Salesperson</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {addUserForm.role === 'other' && (
                  <div className="space-y-3 rounded-md border border-gray-200 p-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Other Role Label
                      </label>
                      <input
                        className="form-input"
                        value={otherRoleLabel}
                        onChange={(e) => setOtherRoleLabel(e.target.value)}
                        placeholder="e.g. Inventory Specialist"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Permissions
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {permissionOptions.map((permission) => (
                          <label
                            key={permission.value}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={otherRolePermissions.includes(permission.value)}
                              onChange={(e) => {
                                setOtherRolePermissions((previous) => {
                                  if (e.target.checked) {
                                    return [...previous, permission.value];
                                  }
                                  return previous.filter((item) => item !== permission.value);
                                });
                              }}
                            />
                            {permission.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className="form-input"
                    value={addUserForm.status}
                    onChange={(e) =>
                      setAddUserForm({
                        ...addUserForm,
                        status: e.target.value as AddUserFormState['status'],
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={closeAddUserModal}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default UsersPage;
